import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  Phone,
  MapPin,
  StickyNote,
  Eye,
  EyeOff,
  ShieldCheck,
} from 'lucide-react';
import type { PatientRow } from '@/hooks/usePatients';
import { PatientPortalSection } from '@/components/patient/PatientPortalSection';
import type { Json } from '@/types/database.generated';

interface AddressShape {
  street: string;
  zip_code: string;
  city: string;
  country: string;
}

function readAddress(value: Json | null): AddressShape {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { street: '', zip_code: '', city: '', country: 'Danmark' };
  }
  const v = value as Record<string, Json>;
  return {
    street: typeof v.street === 'string' ? v.street : '',
    zip_code:
      typeof v.zip_code === 'string' ? v.zip_code : typeof v.zip === 'string' ? v.zip : '',
    city: typeof v.city === 'string' ? v.city : '',
    country: typeof v.country === 'string' ? v.country : 'Danmark',
  };
}

function formatAddress(address: AddressShape): string {
  const parts = [
    address.street,
    [address.zip_code, address.city].filter(Boolean).join(' '),
    address.country,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

interface PatientOverviewPanelProps {
  patient: PatientRow & { has_cpr: boolean };
  canEdit: boolean;
  canRevealCpr: boolean;
  revealedCpr: string | null;
  onRevealCpr: () => void;
  cprLoading: boolean;
}

export function PatientOverviewPanel({
  patient,
  canEdit,
  canRevealCpr,
  revealedCpr,
  onRevealCpr,
  cprLoading,
}: PatientOverviewPanelProps) {
  const { t } = useTranslation();
  const addr = readAddress(patient.address);
  const localeLabel =
    (patient.preferred_locale as string | undefined) === 'en-DK' ? 'EN' : 'DA';

  return (
    <section className="mt-4 rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
      {/* Primary contact — what staff need at a glance */}
      <div className="grid gap-px bg-gray-100 dark:bg-gray-700/50 sm:grid-cols-3">
        <OverviewCell
          icon={<Mail className="h-4 w-4 text-gray-400" />}
          label={t('patients.field_email')}
          value={patient.email ?? '—'}
          href={patient.email ? `mailto:${patient.email}` : undefined}
        />
        <OverviewCell
          icon={<Phone className="h-4 w-4 text-gray-400" />}
          label={t('patients.field_phone')}
          value={patient.phone ?? '—'}
          href={patient.phone ? `tel:${patient.phone}` : undefined}
        />
        <OverviewCell
          icon={<ShieldCheck className="h-4 w-4 text-gray-400" />}
          label={t('patients.section_cpr')}
          value={
            patient.has_cpr
              ? revealedCpr
                ? `${revealedCpr.slice(0, 6)}-${revealedCpr.slice(6)}`
                : '••••••-••••'
              : t('patients.cpr_currently_unset')
          }
          mono={patient.has_cpr}
          action={
            patient.has_cpr && canRevealCpr ? (
              <button
                type="button"
                onClick={onRevealCpr}
                disabled={cprLoading}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50 dark:text-indigo-400"
              >
                {revealedCpr ? (
                  <span className="inline-flex items-center gap-1">
                    <EyeOff className="h-3.5 w-3.5" />
                    {t('patients.cpr_hide')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" />
                    {t('patients.cpr_reveal')}
                  </span>
                )}
              </button>
            ) : undefined
          }
        />
      </div>

      {/* Address + notes */}
      <div className="space-y-3 px-4 py-3">
        <div className="flex items-start gap-2 text-sm">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
          <div>
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t('patients.section_address')}
            </span>
            <p className="mt-0.5 text-gray-900 dark:text-white">{formatAddress(addr)}</p>
          </div>
        </div>

        <div className="flex items-start gap-2 text-sm">
          <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
          <div className="min-w-0 flex-1">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t('patients.section_notes')}
            </span>
            <p
              className={`mt-0.5 text-gray-700 dark:text-gray-200 ${
                patient.notes ? 'line-clamp-2' : 'text-gray-400'
              }`}
            >
              {patient.notes?.trim() || '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Secondary meta — quiet footer, not separate cards */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-100 px-4 py-2.5 text-xs text-gray-500 dark:border-gray-700/80 dark:text-gray-400">
        <MetaChip
          label={t('patients.consent_marketing_email')}
          value={patient.marketing_consent_email ? t('patients.consent_yes') : t('patients.consent_no')}
          active={patient.marketing_consent_email}
        />
        <MetaChip
          label={t('patients.consent_marketing_sms')}
          value={patient.marketing_consent_sms ? t('patients.consent_yes') : t('patients.consent_no')}
          active={patient.marketing_consent_sms}
        />
        <span>
          {t('patients.field_preferred_locale', 'Sprog')}: <strong className="font-medium text-gray-700 dark:text-gray-300">{localeLabel}</strong>
        </span>
        <span className="hidden sm:inline">
          {t('patients.updated_at_label')}: {new Date(patient.updated_at).toLocaleDateString()}
        </span>
        {canEdit && (
          <div className="ml-auto">
            <PatientPortalSection patient={patient} canManage={canEdit} variant="inline" />
          </div>
        )}
      </div>
    </section>
  );
}

function OverviewCell({
  icon,
  label,
  value,
  href,
  mono,
  action,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
  mono?: boolean;
  action?: ReactNode;
}) {
  const content = (
    <span className={`block truncate ${mono ? 'font-mono' : ''}`}>{value}</span>
  );

  return (
    <div className="bg-white px-4 py-3 dark:bg-card-dark">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {icon}
          {label}
        </span>
        {action}
      </div>
      <div className="mt-1 text-sm text-gray-900 dark:text-white">
        {href ? (
          <a href={href} className="hover:text-indigo-600 dark:hover:text-indigo-400">
            {content}
          </a>
        ) : (
          content
        )}
      </div>
    </div>
  );
}

function MetaChip({
  label,
  value,
  active,
}: {
  label: string;
  value: string;
  active: boolean;
}) {
  return (
    <span
      className={
        active
          ? 'rounded-full bg-gray-100 px-2 py-0.5 text-gray-700 dark:bg-gray-700/60 dark:text-gray-200'
          : ''
      }
    >
      {label}: {value}
    </span>
  );
}
